import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getResearch, getResearchFacets, createResearch, patchResearch, deleteResearch, getMe, canEdit,
  type ResearchNote, type ResearchInput, type ResearchKind,
} from '../lib/api.js';
import { Card, PageHeader, Pill, Spinner } from '../components/ui.js';

/**
 * Research repository — a searchable library of the team's in-house knowledge (sector primers,
 * theses, niche notes, external report links). Cross-cutting, not per-deal. The AI assistant also
 * draws on these notes, so what's captured here makes every answer smarter.
 */

const KIND_META: Record<ResearchKind, { label: string; tone: string; icon: string }> = {
  note: { label: 'Note', tone: 'teal', icon: '📝' },
  report: { label: 'Report', tone: 'purple', icon: '📄' },
  link: { label: 'Link', tone: 'navy', icon: '🔗' },
};
const inp = 'px-2.5 py-1.5 rounded-md border border-line bg-white text-[12.5px] text-navy focus:outline-none focus:border-teal';
const btnPrimary = 'px-3 py-1.5 rounded-md text-[12px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50';
const btnGhost = 'px-2.5 py-1 rounded-md text-[11.5px] font-semibold text-navy border border-line bg-white hover:bg-canvas';
const relTime = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const tagList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

function Editor({ initial, submitting, onSubmit, onCancel }: { initial?: ResearchNote; submitting: boolean; onSubmit: (b: ResearchInput) => void; onCancel: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [kind, setKind] = useState<ResearchKind>(initial?.kind ?? 'note');
  const [sector, setSector] = useState(initial?.sector ?? '');
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));
  const [sourceUrl, setSourceUrl] = useState(initial?.sourceUrl ?? '');
  const [pinned, setPinned] = useState(initial?.pinned ?? false);
  const submit = () => { const t = title.trim(); if (!t) return; onSubmit({ title: t, body, kind, sector: sector.trim() || null, tags: tagList(tags), sourceUrl: sourceUrl.trim() || null, pinned }); };
  return (
    <div className="rounded-lg border border-line-2 bg-canvas p-3 space-y-2.5 mb-4">
      <input className={`${inp} w-full text-[14px] font-semibold`} placeholder="Title…" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />
      <textarea className={`${inp} w-full resize-y min-h-[120px] leading-relaxed`} placeholder="Write the note / summary / key takeaways… (searchable, and the AI assistant draws on it)" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Kind
          <select className={inp} value={kind} onChange={(e) => setKind(e.target.value as ResearchKind)}>
            {(Object.keys(KIND_META) as ResearchKind[]).map((k) => <option key={k} value={k}>{KIND_META[k].label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Sector
          <input className={inp} placeholder="e.g. Auto Components" value={sector} onChange={(e) => setSector(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2 flex-1 min-w-[180px]">Tags (comma-separated)
          <input className={`${inp} w-full`} placeholder="thesis, IPO, moat" value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2 flex-1 min-w-[200px]">Source link (optional)
          <input className={`${inp} w-full`} placeholder="https://…" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" className={btnPrimary} disabled={submitting || !title.trim()} onClick={submit}>{submitting ? <span className="inline-flex items-center gap-1.5"><Spinner size={12} /> Saving…</span> : initial ? 'Save' : 'Add to repository'}</button>
        <button type="button" className={btnGhost} onClick={onCancel}>Cancel</button>
        <label className="flex items-center gap-1.5 text-[12px] text-muted cursor-pointer ml-auto"><input type="checkbox" className="h-3.5 w-3.5 accent-teal" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Pin (always surfaced to the AI)</label>
      </div>
    </div>
  );
}

function NoteCard({ note, editable, onEdit, onDelete, onTogglePin, busy }: { note: ResearchNote; editable: boolean; onEdit: () => void; onDelete: () => void; onTogglePin: () => void; busy: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const km = KIND_META[note.kind];
  const long = note.body.length > 320;
  return (
    <div className="rounded-xl border border-line bg-card p-4 shadow-sh hover:shadow-sh2 transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {note.pinned && <span title="Pinned" className="text-gold">★</span>}
          <span className="text-[14px] font-semibold text-navy">{note.title}</span>
          <Pill tone={km.tone}>{km.icon} {km.label}</Pill>
          {note.sector && <Pill tone="gray">{note.sector}</Pill>}
        </div>
        {editable && (
          <div className="flex items-center gap-1.5 flex-none">
            <button className={btnGhost} title={note.pinned ? 'Unpin' : 'Pin'} disabled={busy} onClick={onTogglePin}>{note.pinned ? '★' : '☆'}</button>
            <button className={btnGhost} title="Edit" disabled={busy} onClick={onEdit}>✎</button>
            <button className="px-2 py-1 rounded-md text-[12px] text-muted-2 hover:text-red hover:bg-red-50" title="Delete" disabled={busy} onClick={() => { if (confirm('Delete this note?')) onDelete(); }}>✕</button>
          </div>
        )}
      </div>
      {note.body && <p className="text-[12.5px] text-navy/90 leading-relaxed whitespace-pre-wrap">{expanded || !long ? note.body : note.body.slice(0, 320) + '…'}</p>}
      {long && <button className="text-[11px] font-semibold text-teal-d mt-1" onClick={() => setExpanded((e) => !e)}>{expanded ? 'Show less' : 'Show more'}</button>}
      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-2 text-[11px] text-muted-2">
        {note.author && <span>{note.author.name}</span>}
        <span>· {relTime(note.updatedAt)}</span>
        {note.deal && <Link to={`/deals/${note.deal.id}`} className="text-teal-d hover:underline font-semibold">↗ {note.deal.short}</Link>}
        {note.sourceUrl && <a href={note.sourceUrl} target="_blank" rel="noreferrer" className="text-teal-d hover:underline">source ↗</a>}
        {note.tags.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-[#eef1f5] text-muted">#{t}</span>)}
      </div>
    </div>
  );
}

export function Research() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('');
  const [tag, setTag] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 300_000 });
  const facetsQ = useQuery({ queryKey: ['researchFacets'], queryFn: getResearchFacets });
  const listQ = useQuery({ queryKey: ['research', q, sector, tag], queryFn: () => getResearch({ q, sector, tag }) });
  const editable = canEdit(meQ.data?.identity);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['research'] }); qc.invalidateQueries({ queryKey: ['researchFacets'] }); };
  const createM = useMutation({ mutationFn: (b: ResearchInput) => createResearch(b), onSuccess: () => { invalidate(); setAdding(false); } });
  const patchM = useMutation({ mutationFn: ({ id, b }: { id: string; b: ResearchInput }) => patchResearch(id, b), onSuccess: () => { invalidate(); setEditing(null); } });
  const delM = useMutation({ mutationFn: (id: string) => deleteResearch(id), onSuccess: invalidate });
  const busy = createM.isPending || patchM.isPending || delM.isPending;

  const notes = listQ.data?.notes ?? [];
  const facets = facetsQ.data;
  const editingNote = useMemo(() => notes.find((n) => n.id === editing) ?? null, [notes, editing]);

  return (
    <div className="max-w-[900px]">
      <PageHeader eyebrow="Knowledge" title="Research repository"
        desc="The team's searchable in-house knowledge — sector primers, theses, niche notes & reports. The AI assistant draws on these too."
        actions={editable && <button className={btnPrimary} onClick={() => { setAdding((a) => !a); setEditing(null); }}>{adding ? 'Close' : '＋ Add research'}</button>} />

      {adding && editable && <Editor submitting={createM.isPending} onSubmit={(b) => createM.mutate(b)} onCancel={() => setAdding(false)} />}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input className={`${inp} flex-1 min-w-[200px]`} placeholder="🔍 Search title, body & tags…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={inp} value={sector} onChange={(e) => setSector(e.target.value)}>
          <option value="">All sectors</option>
          {(facets?.sectors ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inp} value={tag} onChange={(e) => setTag(e.target.value)}>
          <option value="">All tags</option>
          {(facets?.tags ?? []).map((t) => <option key={t} value={t}>#{t}</option>)}
        </select>
        {(q || sector || tag) && <button className={btnGhost} onClick={() => { setQ(''); setSector(''); setTag(''); }}>Clear</button>}
      </div>

      {listQ.isLoading ? (
        <div className="flex items-center gap-2 text-muted text-[12.5px] py-6"><Spinner size={14} /> Loading…</div>
      ) : listQ.error ? (
        <Card><p className="text-[12.5px] text-red py-3">Couldn’t load research: {String((listQ.error as Error).message)}</p></Card>
      ) : notes.length === 0 ? (
        <Card><div className="text-center text-muted text-[12.5px] py-8">{q || sector || tag ? 'No research matches your filters.' : `No research captured yet.${editable ? ' Add the first sector primer or thesis above.' : ''}`}</div></Card>
      ) : (
        <div className="space-y-3">
          {(delM.error || patchM.error || createM.error) && <p className="text-[12px] text-red">{String((delM.error || patchM.error || createM.error) as Error)}</p>}
          {notes.map((n) => editing === n.id
            ? <Editor key={n.id} initial={editingNote ?? undefined} submitting={patchM.isPending} onSubmit={(b) => patchM.mutate({ id: n.id, b })} onCancel={() => setEditing(null)} />
            : <NoteCard key={n.id} note={n} editable={editable} busy={busy} onEdit={() => { setEditing(n.id); setAdding(false); }} onDelete={() => delM.mutate(n.id)} onTogglePin={() => patchM.mutate({ id: n.id, b: { pinned: !n.pinned } })} />)}
        </div>
      )}
    </div>
  );
}
